import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { base_api_url } from '../../../environment.jsx';
import { getPaymentStatus } from '../../../services/stripeService';
import './Pricing.css';

const SubscriptionSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const MySwal = withReactContent(Swal);
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [message, setMessage] = useState('Vérification du paiement...');
    const hasVerified = useRef(false); // Flag pour éviter les appels multiples

    // Fonction pour formater la date au format YYYY/MM/DD
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}/${month}/${day}`;
    };

    // Fonction pour ajouter un mois à une date
    const addOneMonth = (date) => {
        const newDate = new Date(date);
        newDate.setMonth(newDate.getMonth() + 1);
        return newDate;
    };

    useEffect(() => {
        const verifyPaymentAndSaveSubscription = async () => {
            if (hasVerified.current) {
                console.log('⏭️ Vérification déjà effectuée, skip');
                return;
            }
            
            hasVerified.current = true;
            
            try {
                const sessionId = searchParams.get('session_id');
                
                if (!sessionId) {
                    setStatus('error');
                    setMessage('Session de paiement introuvable');
                    return;
                }

                // Récupérer les infos du plan depuis localStorage
                const pendingSubscription = JSON.parse(localStorage.getItem('pending_subscription'));
                
                if (!pendingSubscription) {
                    setStatus('error');
                    setMessage('Informations d\'abonnement introuvables');
                    return;
                }

                const { plan } = pendingSubscription;

                // Vérifier le statut du paiement Stripe
                setMessage('Vérification du paiement Stripe...');
                const paymentStatus = await getPaymentStatus(sessionId);
                
                console.log('💳 Payment status:', paymentStatus);

                if (paymentStatus.status !== 'paid') {
                    setStatus('error');
                    setMessage('Le paiement n\'a pas été complété');
                    return;
                }

                // Paiement validé, sauvegarder l'abonnement
                setMessage('Enregistrement de votre abonnement...');
                
                const user = JSON.parse(localStorage.getItem('user'));
                if (!user?.id_utilisateur) {
                    setStatus('error');
                    setMessage('Utilisateur non connecté');
                    return;
                }

                const dateDebut = new Date();
                const dateFin = addOneMonth(dateDebut);

                const abonnementData = {
                    type_abonnement: plan.typeAbonnement,
                    montant: plan.priceValue / 100, // Convertir centimes en euros
                    mode_paiement: 'carte',
                    reference_paiement: sessionId, // Utiliser le session_id comme référence
                    date_debut: formatDate(dateDebut),
                    date_paiement: formatDate(dateDebut),
                    date_fin: formatDate(dateFin)
                };

                console.log('📦 Saving subscription:', abonnementData);

                const token = user.access_token;
                
                const existingSubscription = JSON.parse(localStorage.getItem('last_processed_session'));
                if (existingSubscription === sessionId) {
                    console.log('✅ Subscription already processed for this session');
                    setStatus('success');
                    setMessage('Votre abonnement a été activé avec succès !');
                    setTimeout(() => navigate('/restaurants'), 2000);
                    return;
                }
                
                const response = await axios.post(
                    `${base_api_url}abonnement/abonnement`,
                    abonnementData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.status === 200 || response.status === 201) {
                    console.log('✅ Subscription saved:', response.data);
                    
                    // Mettre à jour localStorage avec le nouvel abonnement
                    user.dernier_abonnement = response.data;
                    localStorage.setItem('user', JSON.stringify(user));
                    
                    // Marquer cette session comme traitée (évite les doublons)
                    localStorage.setItem('last_processed_session', JSON.stringify(sessionId));
                    
                    // Nettoyer les données temporaires
                    localStorage.removeItem('pending_subscription');
                    
                    setStatus('success');
                    setMessage('Votre abonnement a été activé avec succès !');

                    // Afficher le message de succès
                    MySwal.fire({
                        icon: 'success',
                        // title: '🎉 Abonnement activé !',
                        html: `
                            <div style="text-align: center;">
                                <p style="color: #666; margin: 15px 0;">
                                    Votre abonnement <strong>${plan.name}</strong> est maintenant actif.
                                </p>
                                <p style="color: #28a745; font-weight: bold;">
                                    Valide jusqu'au ${formatDate(dateFin)}
                                </p>
                            </div>
                        `,
                        confirmButtonText: 'Accéder à mes restaurants',
                        confirmButtonColor: '#FF6B2C',
                        allowOutsideClick: false,
                    }).then(() => {
                        navigate('/restaurants');
                    });
                } else {
                    throw new Error('Erreur lors de l\'enregistrement');
                }

            } catch (error) {
                console.error('❌ Error:', error);
                
                // Réinitialiser le flag en cas d'erreur pour permettre un retry
                hasVerified.current = false;
                
                setStatus('error');
                setMessage(error.message || 'Une erreur est survenue');
                
                MySwal.fire({
                    icon: 'error',
                    // title: 'Erreur',
                    text: error.message || 'Une erreur est survenue lors de l\'activation de votre abonnement.',
                    confirmButtonText: 'Réessayer',
                    confirmButtonColor: '#FF6B2C',
                }).then(() => {
                    navigate('/pricing');
                });
            }
        };

        verifyPaymentAndSaveSubscription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="pricing-component">
            <div className="pricing-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
                {status === 'loading' && (
                    <>
                        <div style={{ marginBottom: '30px' }}>
                            <div 
                                className="spinner-border text-primary" 
                                role="status" 
                                style={{ width: '4rem', height: '4rem' }}
                            >
                                <span className="sr-only">Chargement...</span>
                            </div>
                        </div>
                        <h2 style={{ color: '#333', marginBottom: '16px' }}>Traitement en cours...</h2>
                        <p style={{ color: '#666', fontSize: '16px' }}>{message}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div style={{ fontSize: '80px', marginBottom: '20px' }}>🎉</div>
                        <h2 style={{ color: '#28a745', marginBottom: '16px' }}>Paiement réussi !</h2>
                        <p style={{ color: '#666', fontSize: '16px' }}>{message}</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div style={{ fontSize: '80px', marginBottom: '20px' }}>❌</div>
                        <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>Erreur</h2>
                        <p style={{ color: '#666', fontSize: '16px' }}>{message}</p>
                        <button
                            onClick={() => navigate('/pricing')}
                            style={{
                                marginTop: '20px',
                                padding: '12px 30px',
                                backgroundColor: '#FF6B2C',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Retour aux tarifs
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default SubscriptionSuccess;
